import { DateTime } from 'luxon'

export interface MuiFilterItem {
  columnField: string
  operatorValue: string
  // biome-ignore lint/suspicious/noExplicitAny: simplicity
  value: any
}

export interface MuiFilterModel {
  items: MuiFilterItem[]
}

export interface ParsedFilter {
  columnField: string
  operatorValue: string
  numberValue?: number
  stringValue?: string
  dateValue?: string
  dateRange?: {
    startDate: string
    endDate: string
  }
}

/**
 * Converts MUI DataGrid filter operations to SQL-compatible operations
 */
export function parseMuiFilterOperationForSql(
  operation: string,
  // biome-ignore lint/suspicious/noExplicitAny: simplicity
  value: any
): string {
  switch (operation) {
    case '=':
    case 'equals':
      return '='

    case '!=':
      return '!='

    case '>':
    case 'after':
      return '>'

    case '>=':
    case 'onOrAfter':
      return '>='

    case '<':
    case 'before':
      return '<'

    case '<=':
    case 'onOrBefore':
      return '<='

    // These dateTime 'is' and 'not' filters _would_ be a one-to-one operation BUT Mui does not
    // support dateTime selection to the seconds resolution, use the BETWEEN SQL function instead
    case 'is':
      if (value === 'true' || value === 'false') {
        return '='
      }
      return 'between'

    case 'not':
      return 'notBetween'

    // Some Mui string filter operations are not a one-to-one mapping to SQL, pass them through.
    // Operations include: contains, startsWith, endsWith, isEmpty, and isNotEmpty
    default:
      return operation
  }
}

/**
 * Parses MUI filter value based on column type and returns the appropriate format
 */
export const parseMuiFilterValueForType = ({
  operatorValue,
  columnField,
  value,
}: {
  operatorValue: string
  columnField: string
  // biome-ignore lint/suspicious/noExplicitAny: simplicity
  value: any
}): Omit<ParsedFilter, 'columnField' | 'operatorValue'> => {
  switch (columnField) {
    case 'id':
      return { numberValue: value }

    case 'amount':
      return { numberValue: value * 100 } // amounts are stored in cents

    case 'updatedAt':
    case 'createdAt':
      // Special case, take advantage of date value being in the format of "YYYY-MM-DDDTHH:MM" and append the seconds
      if (operatorValue === 'is' || operatorValue === 'not') {
        return {
          dateRange: {
            startDate: DateTime.fromISO(value).startOf('day').toISO() || '',
            endDate: DateTime.fromISO(value).endOf('day').toISO() || '',
          },
        }
      }
      return { dateValue: DateTime.fromISO(value).toISO() || undefined }
    case 'isBankError':
      // Handle boolean values for isBankError
      return { stringValue: value?.toString()?.trim() }
    default:
      return { stringValue: value?.toString()?.trim() }
  }
}

/**
 * Parses MUI DataGrid filter model into backend-compatible filter format
 */
export const parseMuiFilterModel = (filterModel?: MuiFilterModel): ParsedFilter[] | undefined => {
  if (!filterModel?.items || filterModel.items.length === 0) {
    return undefined
  }

  return filterModel.items.map(({ columnField, value, operatorValue }) => ({
    columnField,
    operatorValue: parseMuiFilterOperationForSql(operatorValue, value),
    ...parseMuiFilterValueForType({ operatorValue, columnField, value }),
  }))
}
